import {
  Component,
  Input,
  Output,
  OnChanges,
  NgZone,
  EventEmitter,
  ViewChild,
  Inject,
  ElementRef,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { RoundProgressService } from '../round-progress/round-progress.service';
import { roundProgressDefaults, RoundProgressDefaults1 } from '../round-progress/round-progress.config';
import { RoundProgressEase } from '../round-progress/round-progress.ease';

@Component({
  selector: 'round-progress',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './round-progress.component.html',
  styleUrls: ['./round-progress.component.scss'],
  host: {
    'role': 'progressbar',
    '[attr.aria-valuemin]': '0',
    '[attr.aria-valuemax]': 'max',
    '[attr.aria-valuenow]': 'current',
    '[style.width]': 'responsive ? "" : getDiameter() + "px"',
    '[style.height]': 'getElementHeight()',
    '[style.padding-bottom]': 'getPaddingBottom()',
    '[class.responsive]': 'responsive',
  },
  standalone: false
})
export class RoundProgressComponent implements OnChanges {
  private currentLinecap: 'round' | '' = '';

  /** Reference to the underlying `path` node. */
  @ViewChild('path') path!: ElementRef<SVGPathElement>;

  /** Current value of the progress bar. */
  @Input() current: number = 0;

  /** Maximum value of the progress bar. */
  @Input() max: number = 0;

  /** Radius of the circle. */
  @Input() radius: number;

  /** Name of the easing function to use when animating. */
  @Input() animation: string;

  /** Time in millisconds by which to delay the animation. */
  @Input() animationDelay: number;

  /** Duration of the animation. */
  @Input() duration: number;

  /** Width of the circle's stroke. */
  @Input() stroke: number;

  /** Color of the circle. */
  @Input() color: string;

  /** Background color of the circle. */
  @Input() background: string;

  /** Whether the circle should take up the width of its parent. */
  @Input() responsive: boolean;

  /** Whether the circle is filling up clockwise. */
  @Input() clockwise: boolean;

  /** Whether to render a semicircle. */
  @Input() semicircle: boolean;

  /** Whether the tip of the progress should be rounded off. */
  @Input() rounded: boolean;

  /** Emits when a new value has been rendered. */
  @Output() onRender: EventEmitter<number> = new EventEmitter();

  private lastAnimationId = 0;

  constructor(
    private service: RoundProgressService,
    private easing: RoundProgressEase,
    @Inject(roundProgressDefaults) private defaults: RoundProgressDefaults1,
    private ngZone: NgZone
  ) {
    /** Radius of the circle. */
    this.radius = this.defaults.radius!;

    /** Name of the easing function to use when animating. */
    this.animation = this.defaults.animation!;

    /** Time in millisconds by which to delay the animation. */
    this.animationDelay = this.defaults.animationDelay!;

    /** Duration of the animation. */
    this.duration = this.defaults.duration!;

    /** Width of the circle's stroke. */
    this.stroke = this.defaults.stroke!;

    /** Color of the circle. */
    this.color = this.defaults.color!;

    /** Background color of the circle. */
    this.background = this.defaults.background!;

    /** Whether the circle should take up the width of its parent. */
    this.responsive = this.defaults.responsive!;

    /** Whether the circle is filling up clockwise. */
    this.clockwise = this.defaults.clockwise!;

    /** Whether to render a semicircle. */
    this.semicircle = this.defaults.semicircle!;

    /** Whether the tip of the progress should be rounded off. */
    this.rounded = this.defaults.rounded!;
  }

  /** Animates a change in the current value. */
  private animateChange(from: number, to: number): void {
    if (typeof from !== 'number') {
      from = 0;
    }

    to = this.clamp(to);
    from = this.clamp(from);

    const changeInValue = to - from;
    const duration = this.duration;

    // Avoid firing change detection for each of the animation frames.
    this.ngZone.runOutsideAngular(() => {
      const start = () => {
        const startTime = this.service.getTimestamp();
        const id = ++this.lastAnimationId;
        const animation = () => {
          const currentTime = Math.min(this.service.getTimestamp() - startTime, duration);
          const easingFn = this.easing[this.animation as keyof RoundProgressEase];
          const value = easingFn(currentTime, from, changeInValue, duration);

          this.updatePath(value);

          if (this.onRender.observers.length > 0) {
            this.onRender.emit(value);
          }

          if (id === this.lastAnimationId && currentTime < duration) {
            requestAnimationFrame(animation);
          }
        };

        requestAnimationFrame(animation);
      };

      if (this.animationDelay > 0) {
        setTimeout(start, this.animationDelay);
      } else {
        start();
      }
    });
  }

  /** Updates the path apperance. */
  private updatePath(value: number): void {
    if (this.path) {
      const arc = this.service.getArc(value, this.max, this.radius - this.stroke / 2,
        this.radius, this.semicircle);
      const path = this.path.nativeElement;

      // Remove the rounded line cap when the value is zero,
      // because SVG won't allow it to disappear completely.
      const linecap = this.rounded && value > 0 ? 'round' : '';

      // This is called on each animation frame so avoid
      // updating the line cap unless it has changed.
      if (linecap !== this.currentLinecap) {
        this.currentLinecap = linecap;
        path.style.strokeLinecap = linecap;
      }

      path.setAttribute('d', arc);
    }
  }

  /** Clamps a value between the maximum and 0. */
  private clamp(value: number): number {
    return Math.max(0, Math.min(value || 0, this.max));
  }

  /** Determines the SVG transforms for the <path> node. */
  getPathTransform(): string | null {
    const diameter = this.getDiameter();

    if (this.semicircle) {
      return this.clockwise
        ? `translate(0, ${diameter}) rotate(-90)`
        : `translate(${diameter + ',' + diameter}) rotate(90) scale(-1, 1)`;
    } else if (!this.clockwise) {
      return `scale(-1, 1) translate(-${diameter} 0)`;
    }

    return null;
  }

  /** Resolves a color through the service. */
  resolveColor(color: string): string {
    return this.service.resolveColor(color);
  }

  /** Change detection callback. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['current']) {
      this.animateChange(changes['current'].previousValue, changes['current'].currentValue);
    } else {
      this.updatePath(this.current);
    }
  }

  /** Diameter of the circle. */
  getDiameter(): number {
    return this.radius * 2;
  }

  /** The CSS height of the wrapper element. */
  getElementHeight(): string | null {
    if (!this.responsive) {
      return (this.semicircle ? this.radius : this.getDiameter()) + 'px';
    }

    return null;
  }

  /** Viewbox for the SVG element. */
  getViewBox(): string {
    const diameter = this.getDiameter();
    return `0 0 ${diameter} ${this.semicircle ? this.radius : diameter}`;
  }

  /** Bottom padding for the wrapper element. */
  getPaddingBottom(): string | null {
    if (this.responsive) {
      return this.semicircle ? '50%' : '100%';
    }

    return null;
  }
}